import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Button, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MicNoneOutlinedIcon from '@mui/icons-material/MicNoneOutlined';
import PauseOutlinedIcon from '@mui/icons-material/PauseOutlined';
import VolumeUpOutlinedIcon from '@mui/icons-material/VolumeUpOutlined';
import CallEndIcon from '@mui/icons-material/CallEnd';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import { styled } from '@mui/material/styles';
import { Device as VoiceSDK } from '@twilio/voice-sdk';
import logo from '../../assets/dafabet.png';
import logoBig from '../../assets/dafabetBig.png';
import { useSelector } from 'react-redux';
import { selectConfig } from '../../redux/slices/configSlice';
import { selectPlayers, selectPlayersStatus } from '../../redux/slices/playersSlice';
import { getUrlParam, capitalizeWords } from '../../utils/common';

const CustomIconButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: '#FFE000',
  color: '#000',
  borderRadius: '50%',
  width: '80px',
  height: '80px',
  marginTop: '60px',
  [theme.breakpoints.up('md')]: {
    width: '64px',
    height: '64px',
    marginTop: '40px',
  },
}));

// Reuse a slimmed down ip/country hook for call params
const useIpAndCountry = () => {
    const [ipAddress, setIpAddress] = useState('');
    const [countryCode, setCountryCode] = useState('');

    useEffect(() => {
        const fetchIpAndCountry = async () => {
            try {
                // Primary service - ipapi.co (higher rate limits)
                const response = await fetch('https://ipapi.co/json/');
                const data = await response.json();

                if (data.ip && data.country) {
                    setIpAddress(data.ip);
                    setCountryCode(data.country_code);
                    return;
                }

                // Fallback 1 - cloudflare
                const cfResponse = await fetch('https://cloudflare.com/cdn-cgi/trace');
                const cfText = await cfResponse.text();

                if (cfText) {
                    // Parse the response
                    const dataObj = cfText.trim().split('\n').reduce((obj: Record<string, string>, pair) => {
                        const parts = pair.split('=');
                        if (parts.length === 2) {
                            const [key, value] = parts;
                            return { ...obj, [key]: value };
                        }
                        return obj;
                    }, {});

                    if (dataObj.ip) {
                        setIpAddress(dataObj.ip);
                        setCountryCode(dataObj.loc || '');
                        return;
                    }
                }

                // Fallback 2 - geolocation-db.com
                const geoResponse = await fetch('https://geolocation-db.com/json/');
                const geoData = await geoResponse.json();

                if (geoData.IPv4) {
                    setIpAddress(geoData.IPv4);
                    setCountryCode(geoData.country_code);
                }
            } catch (error) {
                console.error('Error fetching IP and Country Code:', error);
                // Last resort - use client-side detection if available
                try {
                    const rtcPeerConnection = new RTCPeerConnection({iceServers: []});
                    rtcPeerConnection.createDataChannel('');
                    rtcPeerConnection.createOffer().then(offer => rtcPeerConnection.setLocalDescription(offer));
                    rtcPeerConnection.onicecandidate = (ice) => {
                        if (ice.candidate) {
                            const ipRegex = /([0-9]{1,3}(\.[0-9]{1,3}){3})/;
                            const match = ipRegex.exec(ice.candidate.candidate);
                            if (match) {
                                setIpAddress(match[1]);
                                rtcPeerConnection.close();
                            }
                        }
                    };
                } catch (e) {
                    console.error('Failed to get IP via WebRTC:', e);
                }
            }
        };

        fetchIpAndCountry();
    }, []);

    return { ipAddress, countryCode };
};

const Voice: React.FC = () => {
  const [device, setDevice] = useState<any>(null);
  const [call, setCall] = useState<any>(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isCallEnded, setIsCallEnded] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showThankYou, setShowThankYou] = useState(false);
  const [volume, setVolume] = useState(1.0);
  const durationRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const didConnectRef = useRef(false);

  const config = useSelector(selectConfig);
  const formData = config?.data?.length ? config.data[0].attributes : null;
  const players = useSelector(selectPlayers);
  const playerSelectStatus = useSelector(selectPlayersStatus);
  const playerData = players?.data ? players?.data.attributes : null;
  const { ipAddress, countryCode } = useIpAndCountry();


  useEffect(() => {
    return () => {
      if (device) {
        try { device.destroy(); } catch {}
      }
      if (durationRef.current) {
        clearInterval(durationRef.current);
      }
    };
  }, [device]);

  const generateWebcallToken = async () => {
    try {
      // Build dynamic params similar to PreChat
      const urlParams = new URLSearchParams(window.location.search);
      const { pathname } = new URL(window.location.href);
      const parts = pathname.split("/").filter(Boolean);
      const locale = parts[3] || formData?.Template?.Language || 'EN';
      let lang = (locale || 'EN').toUpperCase();

      // Generate level from URL param
      const generateUserLevel = () => {
        let level = 'REG';
        const levelInt = getUrlParam('level');
        if (levelInt) {
          if (["17", "26"].includes(levelInt)) level = 'VIP';
          else if (["13", "18"].includes(levelInt)) level = 'Elite';
        }
        return level;
      };

      const playerUsername = playerData?.username || 'Guest';
      const playerEmail = playerData?.email || '';

      // Brand + product
      let brand = formData?.Template?.Brand || 'dafabet';
      const brandMapping: Record<string, string> = { dfbt: 'dafabet', sevenbaby: '777baby' };
      brand = brandMapping[brand] || brand;

      // Skillset similar to prechat
      const appGenerateSkillset = (cat: string) => {
        let c = capitalizeWords(cat);
        if (c === 'Dafabet') c = 'Dafa';
        const options = ['WC', lang, c, '__REG_PI'];
        return options.join('_');
      };
      const skillset = appGenerateSkillset(brand);

      const getReferer = (defaultValue = '') => {
        let referer = document.referrer;
        if (!referer || referer === '') {
          const urlParams2 = new URLSearchParams(window.location.search);
          referer = urlParams2.get('referer') || defaultValue;
        }
        try {
          const url = new URL(referer);
          return `${url.protocol}//${url.host}${url.pathname}`;
        } catch (e) {
          return defaultValue;
        }
      };

      // Additional dynamic values similar to PreChat
      const suggestedPM = getUrlParam('payment') || '';
      const otherPM = getUrlParam('otherpayment') || 'NULL';
      const product = (getUrlParam('product') || '').toLowerCase();
      const inquiry = getUrlParam('inquiry') || 'DP';
      const subinquiry = getUrlParam('subinquiry') || '';
      const deposit = getUrlParam('deposit') || '';

      // Build request payload in the new format
      const requestPayload = {
        formData: {
          friendlyName: playerUsername,
          email: playerEmail,
          query: "Voice call will start now",
          Brand: brand,
          IP: ipAddress || '',
          country: countryCode || '',
          referer: getReferer(''),
          RefererPlatform: navigator.userAgent,
          CustomerVerified: playerData ? 'true' : 'false',
          language: lang,
          CustomerSegment: generateUserLevel(),
          SuggestedPM: suggestedPM,
          OtherPM: otherPM,
          Restart: 'false',
          skillset,
          username: playerUsername,
          name: playerUsername,
          phoneNumber: '',
          product,
          inquiry,
          subinquiry,
          deposit,
        }
      };

      console.log('Sending request to /initWebcall:', requestPayload);

      // Make request to the new endpoint
      const baseUrl = process.env.REACT_APP_TWILIO_API_URL || process.env.REACT_APP_CHATCON_URL || 'http://localhost:3010';
      const response = await fetch(`${baseUrl}/initWebcall`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestPayload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Response from /initWebcall:', data);

      // Extract the required fields from response
      const { token, conversationSid, identity, expiration } = data;

      if (!token) {
        throw new Error('No token received from /initWebcall');
      }

      return {
        tokenResponse: {
          token,
          conversationSid,
          identity,
          expiration,
        },
        requestPayload,
      };

    } catch (error) {
      console.error('Error generating webcall token:', error);
      throw error;
    }
  };

  const initializeVoice = async () => {
    try {
      // Generate token using new endpoint format
      const { tokenResponse, requestPayload } = await generateWebcallToken();
      if (!tokenResponse?.token) {
        console.error('Failed to get token from /initWebcall');
        return;
      }

      console.log('Token response:', tokenResponse);
      const voiceDevice = new VoiceSDK(tokenResponse.token);

      const doConnect = async () => {
        if (didConnectRef.current) return;
        const toEnv = process.env.REACT_APP_CALLING_REMOTE_ADDRESS;
        const toCfg = formData?.CallUs?.CallingRemoteAddress;
        const toNumber = (toEnv || toCfg || '').trim();
        if (!toNumber) return;

        try {
          didConnectRef.current = true;

          // Extract values from requestPayload.formData for use in call parameters
          const {
            friendlyName,
            email,
            Brand,
            IP,
            country,
            referer,
            RefererPlatform,
            CustomerVerified,
            language,
            CustomerSegment,
            SuggestedPM,
            OtherPM,
            skillset,
            username,
            name,
            phoneNumber,
            product,
            inquiry,
            subinquiry,
            deposit
          } = requestPayload.formData;

          // Use the token response data and requestPayload values for call parameters
          const params: Record<string, any> = {
            To: toNumber,
            identity: tokenResponse.identity || playerData?.username || 'Guest',
            conversationSid: tokenResponse.conversationSid,
            // Add requestPayload values as variables
            friendlyName,
            email,
            Brand,
            IP,
            country,
            referer,
            RefererPlatform,
            CustomerVerified,
            language,
            CustomerSegment,
            SuggestedPM,
            OtherPM,
            skillset,
            username,
            name,
            phoneNumber,
            product,
            inquiry,
            subinquiry,
            deposit
          };

          console.log('Connecting call with params:', params);
          const callInstance = await voiceDevice.connect({ params });
          setCall(callInstance);

          // Setup audio context for volume control
          const setupAudioContext = () => {
            try {
              const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
              audioContextRef.current = audioContext;

              // Create gain node for volume control
              const gainNode = audioContext.createGain();
              gainNode.gain.value = volume;
              gainNodeRef.current = gainNode;

              // Connect to Twilio's audio stream when available
              if (callInstance.getRemoteStream) {
                const remoteStream = callInstance.getRemoteStream();
                if (remoteStream) {
                  const source = audioContext.createMediaStreamSource(remoteStream);
                  source.connect(gainNode);
                  gainNode.connect(audioContext.destination);
                }
              }
            } catch (err) {
              console.log('Audio context setup error:', err);
            }
          };

          // Start timer when call is accepted by remote party
          callInstance.on('accept', () => {
            console.log('Call accepted - starting timer');
            durationRef.current = setInterval(() => setElapsedTime((t) => t + 1), 1000);
            setupAudioContext();
          });

          callInstance.on('callEnded', () => {
            console.log("Call ended");
            endCall();
          });

          callInstance.on('disconnect', () => {
            console.log('Call disconnected');
            endCall();
          });
        } catch {}
      };

      voiceDevice.on('registered', () => {
        //setIsAuthorized(true);
        doConnect();
      });

      voiceDevice.on('error', (err: any) => {
        // mute UI errors in this flow; we show only UX states
      });



      // Device-level disconnect handler (for device errors)
      voiceDevice.on('disconnect', () => {
        console.log('Device disconnected');
        endCall();
        // Don't override call-specific disconnect handling
      });

      await voiceDevice.register();
      setDevice(voiceDevice);


    } catch (err) {
      // swallow init errors; UI will remain unauth view
    }
  };

  // determine authorization based on URL param 's' (same as legacy call page)
  useEffect(() => {
    const sessionCode = new URLSearchParams(window.location.search).get('s');
    if (sessionCode) {
      setIsAuthorized(true);
    }
  }, []);



  // init voice device on mount regardless; registration will flip authorized state
  useEffect(() => {
    if (!device && isAuthorized && (playerSelectStatus === 'succeeded' || playerSelectStatus === 'failed' )  && ipAddress!="") {
      initializeVoice();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorized, playerSelectStatus, ipAddress]);

  const endCall = async () => {
    console.log('End call button clicked');
    try {
      if (call) {
        console.log('Disconnecting call...');

        // Multiple methods to ensure call is terminated
        try {
          // Method 1: Standard disconnect
          await call.disconnect();
          console.log('Call.disconnect() completed');
        } catch (e) {
          console.log('Call.disconnect() failed:', e);
        }

        try {
          // Method 2: Force reject if still connected
          if (call.status && call.status() !== 'closed') {
            call.reject();
            console.log('Call.reject() executed');
          }
        } catch (e) {
          console.log('Call.reject() failed:', e);
        }

        try {
          // Method 3: Mute all audio immediately
          call.mute(true);
          console.log('Call muted');
        } catch (e) {
          console.log('Call.mute() failed:', e);
        }
      }

      // Force cleanup of device if available
      if (device) {
        try {
          device.disconnectAll();
          console.log('Device.disconnectAll() executed');
        } catch (e) {
          console.log('Device.disconnectAll() failed:', e);
        }
      }

      // Immediately clean up and show thank you page
      console.log('Cleaning up call state...');
      setCall(null);
      setIsCallEnded(true);
      setShowThankYou(true);

      if (durationRef.current) {
        clearInterval(durationRef.current);
        durationRef.current = null;
      }

      // Clean up audio context aggressively
      if (audioContextRef.current) {
        try {
          // Stop all audio nodes
          if (gainNodeRef.current) {
            gainNodeRef.current.disconnect();
            gainNodeRef.current = null;
          }
          // Close audio context
          await audioContextRef.current.close();
          audioContextRef.current = null;
          console.log('Audio context closed');
        } catch (e) {
          console.log('Audio context cleanup failed:', e);
        }
      }

      // Stop all media streams
      try {
        navigator.mediaDevices.getUserMedia({ audio: true })
          .then(stream => {
            stream.getTracks().forEach(track => {
              track.stop();
              console.log('Media track stopped');
            });
          })
          .catch(() => {}); // Ignore errors
      } catch (e) {}

      setElapsedTime(0);
      setIsMuted(false);
      setIsOnHold(false);
      setVolume(1.0);

      console.log('Thank you page should be showing now');

    } catch (err) {
      console.error('Error ending call:', err);
      // Even if there's an error, show thank you page
      setCall(null);
      setIsCallEnded(true);
      setShowThankYou(true);
    }
  };


  const toggleMute = () => {
    if (!call) return;
  
    const newMuted = !isMuted;
  
    try {
      // Preferred (modern SDK)
      const handler = call.mediaHandler || call._mediaHandler;
      const localStream = handler?._localStream || handler?.stream;
  
      // Legacy fallbacks (older SDKs)
      const fallbackStream =
        call.mediaStream?.localStream ||
        call._mediaStream?.localStream ||
        call._mediaStream;
  
      const stream = localStream || fallbackStream;
  
      if (!stream) {
        console.warn("No local stream found for mute");
        console.log("call object:", call); // <-- this will show what fields actually exist
        return;
      }
  
      const audioTracks: MediaStreamTrack[] = stream.getAudioTracks();

      audioTracks.forEach(track => {
        track.enabled = !newMuted;
      });

      console.error("Audio muted:", newMuted);
      setIsMuted(newMuted);
    } catch (err) {
      console.error("Mute error:", err);
    }
  };
  

  const toggleHold = () => {
    console.log("Hold clicked. Current state:", isOnHold);
  
    if (!call) {
      console.log("No active call to hold/unhold");
      return;
    }
  
    try {
      const newHoldState = !isOnHold;
      setIsOnHold(newHoldState);
  
      const localStream = call.getLocalStream?.();
      const remoteAudioEl = call._mediaStream?._audioElement; // <-- IMPORTANT
  
      if (newHoldState) {
        // --- PUT CALL ON HOLD ---
        console.log("Placing call on hold...");
  
        // Disable mic (outgoing audio)
        localStream?.getAudioTracks().forEach((track: MediaStreamTrack) => {
          track.enabled = false;
        });
  
        // Disable remote audio (incoming audio)
        if (remoteAudioEl) remoteAudioEl.muted = true;
  
        // Optional input volume to 0
        try {
          device?.audio?.setInputVolume?.(0);
        } catch {}
  
        console.log("Call is now on hold (both directions muted).");
      } else {
        // --- RESUME CALL ---
        console.log("Resuming call from hold...");
  
        // Restore remote audio
        if (remoteAudioEl) remoteAudioEl.muted = false;
  
        // Restore mic only if user is not muted
        if (!isMuted) {
          localStream?.getAudioTracks().forEach((track : MediaStreamTrack) => {
            track.enabled = true;
          });
  
          try {
            device?.audio?.setInputVolume?.(1.0);
          } catch {}
        }
  
        console.log("Call resumed.");
      }
  
    } catch (err) {
      console.error("Hold toggle error:", err);
    }
  };
  
  

  const adjustVolume = () => {
    console.log('Volume button clicked, current volume:', volume);
    if (!call) {
      console.log('No call available for volume adjustment');
      return;
    }
    try {
      // Cycle through volume levels: 25% -> 50% -> 75% -> 100% -> 125% -> 150% -> 25%
      let newVolume: number;
      if (volume <= 0.25) newVolume = 0.5;
      else if (volume <= 0.5) newVolume = 0.75;
      else if (volume <= 0.75) newVolume = 1.0;
      else if (volume <= 1.0) newVolume = 1.25;
      else if (volume <= 1.25) newVolume = 1.5;
      else newVolume = 0.25;

      console.log('Setting volume to:', newVolume);
      setVolume(newVolume);

      // Method 1: Control Twilio Device audio output volume (primary method)
      if (device && device.audio && device.audio.setOutputVolume) {
        try {
          device.audio.setOutputVolume(Math.min(newVolume, 1.0)); // Device max is 1.0
          console.log('Twilio device output volume set to:', Math.min(newVolume, 1.0));
        } catch (e) {
          console.log('Device audio output control not available');
        }
      }

      // Method 2: Control Web Audio API gain (for amplification beyond 100%)
      if (gainNodeRef.current) {
        gainNodeRef.current.gain.value = newVolume;
        console.log(`Web Audio gain adjusted to: ${(newVolume * 100).toFixed(0)}%`);
      } else {
        console.log('No gain node available - will setup on next call');
      }

      // Method 3: Control HTML5 audio element volume
      if (audioRef.current) {
        audioRef.current.volume = Math.min(newVolume, 1.0);
        console.log('HTML5 audio volume set to:', Math.min(newVolume, 1.0));
      }

      // Method 4: Control Twilio call volume (if available)
      if (call && typeof call.volume === 'function') {
        try {
          call.volume(Math.min(newVolume, 1.0));
          console.log('Twilio call volume set to:', Math.min(newVolume, 1.0));
        } catch (e) {
          console.log('Call volume method not available');
        }
      }

      // Method 4: Try to control media stream tracks
      if (call && call.getRemoteStream) {
        const remoteStream = call.getRemoteStream();
        if (remoteStream) {
          remoteStream.getAudioTracks().forEach((track: MediaStreamTrack) => {
            if ('volume' in track) {
              (track as any).volume = newVolume;
            }
          });
        }
      }

      // Method 5: Create new audio context with updated gain if needed
      if (!gainNodeRef.current && audioContextRef.current) {
        try {
          const gainNode = audioContextRef.current.createGain();
          gainNode.gain.value = newVolume;
          gainNodeRef.current = gainNode;

          if (call && call.getRemoteStream) {
            const remoteStream = call.getRemoteStream();
            if (remoteStream) {
              const source = audioContextRef.current.createMediaStreamSource(remoteStream);
              source.connect(gainNode);
              gainNode.connect(audioContextRef.current.destination);
            }
          }
        } catch (err) {
          console.log('Audio context gain setup error:', err);
        }
      }

      console.log(`Volume adjusted to: ${(newVolume * 100).toFixed(0)}%`);

      // Visual feedback - briefly highlight the button
      const volumeButton = document.querySelector('[data-volume-button]') as HTMLElement;
      if (volumeButton) {
        volumeButton.style.transform = 'scale(1.1)';
        setTimeout(() => {
          volumeButton.style.transform = 'scale(1)';
        }, 150);
      }

    } catch (err) {
      console.log('Volume adjustment error:', err);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Box
      sx={{
        backgroundColor: 'primary.main',
        width: '100%',
        height: '100%',
        position: 'absolute',
        display: 'flex',
        justifyContent: 'space-between',
        top: '0',
        padding: '16px',
      }}
    >
      {/* Hidden audio element for volume control */}
      <audio ref={audioRef} style={{ display: 'none' }} />

      {!isAuthorized ? (
        <Box sx={{ textAlign: 'center', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-around' }}>
          <Button
            variant="outlined"
            onClick={() => window.history.back()}
            sx={{
              color: 'secondary.light',
              border: '3px solid',
              borderRadius: '50%',
              minWidth: 'auto',
              width: '35px',
              height: '35px',
              position: 'absolute',
              right: '16px',
              top: '16px',
            }}
          >
            <CloseIcon />
          </Button>

          <Box>
            <Box
              component="img"
              sx={{
                width: '80%',
                maxWidth: '380px',
                height: 'fit-content',
              }}
              alt="Sample image"
              src={logo}
            />

            <Typography sx={{ color: '#fff', fontWeight: 'bold' }} mt={4} mb={2} fontSize={24}>
              {formData?.WebRTC?.UnAuthWndowText}
            </Typography>

            <Button
              variant="contained"
              onClick={() => {
                window.location.href = 'https://www.dafabet.com/en/';
              }}
              sx={{ background: '#fff', borderRadius: '24px', color: '#000', fontWeight: 'bold', textTransform: 'capitalize', fontSize: '18px' }}
            >
              {formData?.WebRTC?.LoginButtonText}
            </Button>
          </Box>

          <Box>
            <Typography sx={{ color: '#fff', fontWeight: 'bold' }} mt={4} mb={2} fontSize={24}>
              {formData?.WebRTC?.UnAuthWndowText2}
            </Typography>
            <Button
              onClick={() => {
                window.location.href = formData?.WebRTC?.UnAuthChatUrl || '/';
              }}
              variant="contained"
              sx={{ background: '#FFE000', color: '#000', fontWeight: 'bold', textTransform: 'capitalize', fontSize: '18px', width: '300px' }}
            >
              {formData?.WebRTC?.UnAuthChatButtonText}
            </Button>
          </Box>
        </Box>
      ) : null}

      {isAuthorized && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
          <Box sx={{ textAlign: 'center', width: { xs: '100%', md: 520 }, p: { xs: 0, md: 4 }, borderRadius: { xs: 0, md: 3 }, boxShadow: { xs: 'none', md: '0 8px 24px rgba(0,0,0,0.25)' }, backgroundColor: { xs: 'transparent', md: 'rgba(0,0,0,0.15)' } }}>
            <Box
              component="img"
              sx={{
                width: { xs: '60%', md: '40%' },
                maxWidth: { xs: '380px', md: '220px' },
                height: 'fit-content',
              }}
              alt="Sample image"
              src={logoBig}
            />

            {!isCallEnded && (
              <Box>
                {!call && (
                  <Typography sx={{ color: '#FFE000' }} mt={4} mb={2} fontSize={24}>
                    {formData?.WebRTC?.ConnectingText}
                  </Typography>
                )}

                {call && (
                  <Box>
                    <Typography sx={{ color: '#FFE000', textTransform: 'uppercase', fontWeight: 'bold' }} mt={2} mb={1} fontSize={{ xs: 36, md: 28 }}>
                      {playerData?.username || 'Guest'}
                    </Typography>
                    <Typography sx={{ color: '#FFE000' }} fontSize={{ xs: 24, md: 18 }}>
                      calling via Support Line
                    </Typography>

                    {elapsedTime > 0 && (
                      <Typography sx={{ color: '#FFF', textTransform: 'uppercase', fontWeight: 'bold' }} fontSize={{ xs: 40, md: 28 }} mt={4}>
                        {formatTime(elapsedTime)}
                      </Typography>
                    )}
                  </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-around' }} mt={4}>
                  <Button
                    variant="outlined"
                    onClick={(e) => {
                      console.log('Mute button DOM click event');
                      e.preventDefault();
                      e.stopPropagation();
                      toggleMute();
                    }}
                    sx={{
                      color: isMuted ? 'error.main' : 'secondary.light',
                      border: '3px solid',
                      borderColor: isMuted ? 'error.main' : 'secondary.light',
                      borderRadius: '50%',
                      minWidth: 'auto',
                      width: { xs: '80px', md: '64px' },
                      height: { xs: '80px', md: '64px' },
                      backgroundColor: isMuted ? 'rgba(255,0,0,0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: isMuted ? 'rgba(255,0,0,0.2)' : 'rgba(255,255,255,0.1)',
                      },
                    }}
                  >
                    <MicNoneOutlinedIcon sx={{ fontSize: '20px', width: 'auto', height: 'auto' }} />
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={(e) => {
                      console.log('Hold button DOM click event');
                      e.preventDefault();
                      e.stopPropagation();
                      toggleHold();
                    }}
                    sx={{
                      color: isOnHold ? 'warning.main' : 'secondary.light',
                      border: '3px solid',
                      borderColor: isOnHold ? 'warning.main' : 'secondary.light',
                      borderRadius: '50%',
                      minWidth: 'auto',
                      width: { xs: '80px', md: '64px' },
                      height: { xs: '80px', md: '64px' },
                      backgroundColor: isOnHold ? 'rgba(255,165,0,0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: isOnHold ? 'rgba(255,165,0,0.2)' : 'rgba(255,255,255,0.1)',
                      },
                    }}
                  >
                    <PauseOutlinedIcon sx={{ fontSize: '20px', width: 'auto', height: 'auto' }} />
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={(e) => {
                      console.log('Volume button DOM click event');
                      e.preventDefault();
                      e.stopPropagation();
                      adjustVolume();
                    }}
                    data-volume-button
                    sx={{
                      color: 'secondary.light',
                      border: '3px solid',
                      borderRadius: '50%',
                      minWidth: 'auto',
                      width: { xs: '80px', md: '64px' },
                      height: { xs: '80px', md: '64px' },
                      position: 'relative',
                      transition: 'transform 0.15s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.1)',
                      },
                    }}
                  >
                    <VolumeUpOutlinedIcon sx={{ fontSize: '20px', width: 'auto', height: 'auto' }} />
                    {/* Volume indicator */}
                    <Typography
                      sx={{
                        position: 'absolute',
                        bottom: '-24px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        color: volume > 1.0 ? '#FFE000' : 'secondary.light',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        textShadow: volume > 1.0 ? '0 0 4px rgba(255,224,0,0.5)' : 'none'
                      }}
                    >
                      {(volume * 100).toFixed(0)}%
                    </Typography>
                  </Button>
                </Box>

                <CustomIconButton
                  onClick={endCall}
                  sx={{
                    backgroundColor: '#FFE000',
                    '&:hover': { backgroundColor: '#E6C700' }
                  }}
                >
                  <CallEndIcon sx={{ fontSize: '20px', width: 'auto', height: 'auto' }} />
                </CustomIconButton>
              </Box>
            )}

            {(isCallEnded || showThankYou) && (
              <Box sx={{ textAlign: 'center', mt: 8 }}>
                <Typography sx={{ color: '#FFE000', fontWeight: 'bold', mb: 3 }} fontSize={{ xs: 32, md: 28 }}>
                  Thank You
                </Typography>

                <Typography sx={{ color: '#FFE000', mb: 6 }} fontSize={{ xs: 18, md: 20 }}>
                  Call Again
                </Typography>

                <CustomIconButton
                  onClick={() => window.location.reload()}
                  sx={{
                    backgroundColor: '#FFE000',
                    '&:hover': { backgroundColor: '#E6C700' }
                  }}
                >
                  <LocalPhoneIcon sx={{ fontSize: '24px', width: 'auto', height: 'auto' }} />
                </CustomIconButton>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Voice;

